import { parse } from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';

export interface LocalTimeConversion {
  fullLocalized: string;
  filenameTimestamp: string;
  timeOnly: string;
  dateTimeFormatted: string;
}

/**
 * Converts a UTC time string to the user's local timezone and returns various formatted representations.
 * @param timeString - The UTC time string in the format "dd-MM-yyyy'T'HH:mm".
 * @returns An object with multiple formatted date/time strings, or undefined if parsing fails.
 */
export const convertToLocalTime = (timeString?: string): LocalTimeConversion | undefined => {
  if (!timeString) return undefined;

  try {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const parsedDate = parse(timeString, "dd-MM-yyyy'T'HH:mm", new Date());

    const utcDate = new Date(
      Date.UTC(
        parsedDate.getFullYear(),
        parsedDate.getMonth(),
        parsedDate.getDate(),
        parsedDate.getHours(),
        parsedDate.getMinutes()
      )
    );

    const localTime = toZonedTime(utcDate, userTimezone);

    return {
      fullLocalized: format(localTime, 'PPpp', { timeZone: userTimezone }),
      filenameTimestamp: format(localTime, 'yyyy-MM-dd_hh-mm_a', { timeZone: userTimezone }),
      timeOnly: format(localTime, 'hh:mm a', { timeZone: userTimezone }),
      dateTimeFormatted: format(localTime, 'MM.dd.yyyy hh:mm a', { timeZone: userTimezone }),
    };
  } catch (error) {
    console.error('Error parsing date:', error);
    return undefined;
  }
};

/**
 * Returns a filename-safe timestamp string based on the provided UTC timestamp.
 * If the timestamp is invalid or undefined, returns today's date in 'YYYY-MM-DD' format.
 * @param timestamp - The UTC time string in the format "dd-MM-yyyy'T'HH:mm".
 * @returns A string suitable for use in filenames.
 */
export const getValidFilenameTimestamp = (timestamp?: string): string => {
  const convertedTime = convertToLocalTime(timestamp);

  if (convertedTime === undefined) {
    console.error('Invalid timestamp');
    return new Date().toISOString().slice(0, 10);
  }

  return convertedTime.filenameTimestamp;
};
